import { useState, useEffect, useMemo } from 'react';
import { Subscription, getSubscriptions } from '../api/adminApi';
import styles from './SubscriptionsPage.module.css';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

function countryName(code: string): string {
  try { return regionNames.of(code) ?? code; } catch { return code; }
}

import { flagEmoji as countryFlag } from '../utils/flag';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'country' | 'email'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    getSubscriptions()
      .then(setSubscriptions)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function toggleSort(col: typeof sortBy) {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return subscriptions
      .filter((s) =>
        !q || s.email.toLowerCase().includes(q) || s.countryCode.toLowerCase().includes(q) || countryName(s.countryCode).toLowerCase().includes(q)
      )
      .sort((a, b) => {
        let cmp = 0;
        if (sortBy === 'date') cmp = a.subscribedAt.localeCompare(b.subscribedAt);
        else if (sortBy === 'country') cmp = a.countryCode.localeCompare(b.countryCode);
        else cmp = a.email.localeCompare(b.email);
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [subscriptions, search, sortBy, sortDir]);

  const byCountry = useMemo(() => {
    const counts: Record<string, number> = {};
    subscriptions.forEach((s) => { counts[s.countryCode] = (counts[s.countryCode] ?? 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [subscriptions]);

  function SortArrow({ col }: { col: typeof sortBy }) {
    if (sortBy !== col) return <span className={styles.sortNone}>⇅</span>;
    return <span className={styles.sortActive}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <h2 className={styles.heading}>Waitlist</h2>
          {!loading && (
            <span className={styles.badge}>{subscriptions.length}</span>
          )}
        </div>
        <input
          className={styles.search}
          type="search"
          placeholder="Search email or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className={styles.errorBar}>{error}</div>}

      {loading ? (
        <div className={styles.loadingWrap}><div className={styles.spinner} /></div>
      ) : subscriptions.length === 0 ? (
        <div className={styles.empty}>No waitlist entries yet.</div>
      ) : (
        <div className={styles.layout}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} onClick={() => toggleSort('email')}>
                    Email <SortArrow col="email" />
                  </th>
                  <th className={styles.th} onClick={() => toggleSort('country')}>
                    Country <SortArrow col="country" />
                  </th>
                  <th className={styles.th} onClick={() => toggleSort('date')}>
                    Date <SortArrow col="date" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i} className={styles.row}>
                    <td className={styles.td}>{s.email}</td>
                    <td className={styles.td}>
                      <span className={styles.countryCell}>
                        <span className={styles.flag}>{countryFlag(s.countryCode)}</span>
                        <span className={styles.countryName}>{countryName(s.countryCode)}</span>
                        <span className={styles.countryCode}>{s.countryCode}</span>
                      </span>
                    </td>
                    <td className={`${styles.td} ${styles.date}`}>{formatDate(s.subscribedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && search && (
              <div className={styles.noResults}>No results for "{search}"</div>
            )}
          </div>

          <aside className={styles.sidebar}>
            <h3 className={styles.sidebarTitle}>By country</h3>
            <ul className={styles.countryList}>
              {byCountry.map(([code, count]) => (
                <li key={code} className={styles.countryItem}>
                  <span className={styles.sidebarCountry}>
                    <span className={styles.flag}>{countryFlag(code)}</span>
                    <span className={styles.sidebarCountryName}>{countryName(code)}</span>
                  </span>
                  <span className={styles.countryCount}>{count}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      )}
    </div>
  );
}
